/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component, HostListener } from '@angular/core';
import { type Polling, type PromptConfigFull, CoreEventServiceKeyE, DialogTypeE, EventService, PermissionService, PollingService, PromptService } from '@dm/core';
import { type DeploymentStatus } from 'app/common/types/mo';
import { SWITCH } from 'app/config';

import { DeploymentStatusE, DEVICE_MODE_POLLING_TIME } from './deployment-mode.const';
import { DeploymentModeService } from './deployment-mode.service';
import { DeploymentModeStrService } from './deployment-mode-str.service';

@Component({
    selector: 'app-deployment-mode',
    templateUrl: './deployment-mode.component.html',
    styleUrls: ['./deployment-mode.component.scss'],
})
export class DeploymentModeComponent implements OnInit, OnDestroy {
    /**
     * 是否是开局模式
     */
    isInDeployment = false;
    /**
     * 轮询
     */
    polling$: Polling;
    /**
     * 资料
     */
    i18n = this.str.i18n;
    /**
     * 开局模式告警说明
     */
    deploymentModeWarningContent = this.i18n.deploymentModeWarning;
    /**
     * 是否是超级管理员
     */
    isSuperAdmin = this.permissionService.isSuperAdmin();
    /**
     * 结束按钮忙等
     */
    btnLoading = false;

    SWITCH = SWITCH;

    constructor(
        private str: DeploymentModeStrService,
        private service: DeploymentModeService,
        private pollingService: PollingService,
        private eventService: EventService,
        private permissionService: PermissionService,
        private promptService: PromptService,
    ) { }

    /**
     * 检测header上方提示信息的变更，可能导致高度变化，需适配部分场景的显示优化
     * 场景包括：侧滑框（极简模式自定义侧滑框）、气泡toast、高级搜索全局模式、登陆信息等场景的top值
     * 窗口尺寸变化可能导致提示信息自动换行，高度发生变化，也需要重新适配
     */
    @HostListener('window:resize')
    adjustChangeOfTipsOverHeader(): void {
        // 异步执行，确保元素页面的呈现已经变化
        setTimeout(() => {
            this.eventService.emit(CoreEventServiceKeyE.CHECK_TIPS_OVER_HEADER);
        });
    }

    ngOnInit(): void {
        this.getData();
    }

    /**
     * 查询设备模式，检查设备模式是否改变
     */
    getData(): void {
        this.polling$ = this.pollingService.add(this.service.getDeploymentModeRest(), DEVICE_MODE_POLLING_TIME);
        this.polling$.subscribe(
            ({ data }: { data: DeploymentStatus }) => {
                if (!data || !data[0]) {
                    this.showChange(false);
                }

                // 设备模式变化，需要更新侧滑框和开局模式告警的显示或隐藏
                if ((Number(data[0].deploymentStatus) === DeploymentStatusE.IN_DEPLOYMENT) !== this.isInDeployment) {
                    this.showChange(!this.isInDeployment);
                    return;
                }
            },
            () => {
                this.showChange(false);
            },
        );
    }

    /**
     * 更新开局模式告警的显示，更新header及上方提示信息高度
     * @param isInDeployment 是否是开局中
     */
    showChange(isInDeployment: boolean): void {
        this.isInDeployment = isInDeployment;
        this.adjustChangeOfTipsOverHeader();
    }

    /**
     * 结束远程开局
     */
    endRemoteDeployment(): void {
        const prompts: PromptConfigFull[] = [{
            id: 'deployment-mode-end-warning',
            opType: 'warning',
            dialogType: SWITCH.IS_SIMPLE_MODE ? DialogTypeE.SIMPLE_MODE : DialogTypeE.DEFAULT,
            title: this.i18n.deploymentModeEndTitle,
            content: this.i18n.deploymentModeEndWarning,
        }];

        this.promptService.multi(prompts, () => {
            this.promptService.close();
            this.sendRequest();
        }, () => {
            this.promptService.close();
        });
    }

    /**
     * 下发请求
     */
    sendRequest(): void {
        this.btnLoading = true;

        this.service.endRemoteDeployment().subscribe(
            () => {
                this.btnLoading = false;
                this.showChange(false);
            },
            () => {
                this.btnLoading = false;
            },
        );
    }

    ngOnDestroy(): void {
        this.pollingService.remove(this.polling$);
        this.polling$ = null;
    }
}
